import { Aide } from '@/components/ui/aide';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { KeyRound, Plus, Search, X } from 'lucide-react';

import { printingAPI, rentalsAPI, trucksAPI } from '../lib/api';
import { fetchAllPages } from '../lib/fetchAllPages';
import { Button } from '../components/ui/button';
import { EmptyState } from '../components/ui/empty-state';
import { FieldError } from '../components/ui/field-error';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { LoadingState } from '../components/ui/loading-state';
import { PageHeader } from '../components/ui/page-header';
import { PrintMenu } from '../components/business/PrintMenu';
import { SectionCard } from '../components/dashboard';
import InboundRentalsTab from '../components/rentals/InboundRentalsTab';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { SearchableSelect } from '../components/ui/searchable-select';
import { Textarea } from '../components/ui/textarea';
import { toast } from '../components/ui/sonner';
import { ChampsAvances } from '../components/ui/champs-avances';
import { useDebouncedValue } from '../hooks/use-debounced-value';
import { BarrePagination } from '../components/ui/pagination-bar';
import { usePaginationServeur } from '../hooks/usePagination';
import { useDerniereDemande } from '../hooks/useDerniereDemande';
import { appliquerPrixPropose, usePrixPropose } from '../hooks/usePrixPropose';
import { truckOption } from '../lib/selectOptions';
import { cn } from '../lib/utils';
import { formatDateOuTiret, formatMontant } from '../lib/formats';
import { messageErreur } from '../lib/erreurs';
import { libelle } from '../lib/libelles';
import { useRafraichirSur } from '../hooks/useRafraichirSur';

export const PRICING_MODES = [
  { value: 'period', label: 'À la période', hint: 'Jour, semaine ou mois' },
  { value: 'rotation', label: 'À la rotation', hint: 'Forfait par voyage' },
  { value: 'liter', label: 'Au litre', hint: 'Prix du litre transporté' },
];

export const PERIOD_UNITS = [
  { value: 'day', label: 'Jour' },
  { value: 'week', label: 'Semaine' },
  { value: 'month', label: 'Mois' },
];

export const CONTRACT_STATUSES = [
  { value: 'draft', label: 'Brouillon' },
  { value: 'active', label: 'En cours' },
  { value: 'suspended', label: 'Suspendu' },
  { value: 'returned', label: 'Restitué' },
  { value: 'closed', label: 'Clos' },
  { value: 'cancelled', label: 'Annulé' },
];

// La couleur marque un état, jamais une position dans la liste: seul ce qui
// demande une décision (suspendu) ou engage un camion (en cours) est coloré.
// Les quatre autres statuts sont des faits classés, pas des alertes: ils
// partagent volontairement la même teinte neutre.
const STATUS_STYLES = {
  draft: 'bg-muted text-muted-foreground border-border',
  active: 'bg-success/15 text-success border-success/40',
  suspended: 'bg-flame/15 text-flame-ink border-flame/40',
  returned: 'bg-muted text-muted-foreground border-border',
  closed: 'bg-muted text-muted-foreground border-border',
  cancelled: 'bg-muted text-muted-foreground border-border',
};

export const statusLabel = (value) => libelle('rentalStatus', value);

export const pricingLabel = (contract) => {
  const mode = PRICING_MODES.find((item) => item.value === contract?.pricing_mode);
  if (contract?.pricing_mode === 'period') {
    const unit = PERIOD_UNITS.find((item) => item.value === contract?.period_unit);
    return unit ? `Par ${unit.label.toLowerCase()}` : mode?.label || '—';
  }
  return mode?.label || '—';
};

// Le montant s'écrit « 1 250 000 FCFA » : jamais le code de devise.
export const formatMoney = (amount, currency = 'XOF') => formatMontant(amount, currency);

const aujourdHui = () => new Date().toISOString().slice(0, 10);

/**
 * Le contrat neuf, tel qu'il s'ouvre.
 *
 * `start_date` part à la date du jour et non vide: un contrat se saisit le
 * jour où il commence, et le champ obligatoire laissé à blanc ne faisait
 * qu'ajouter un clic dans un calendrier pour y choisir aujourd'hui. La date
 * reste modifiable — un contrat signé la semaine dernière se saisit encore.
 */
const formulaireVide = () => ({
  truck_id: '',
  lessee_label: '',
  pricing_mode: 'period',
  period_unit: 'day',
  rate_amount: '',
  included_units: '',
  overage_rate_amount: '',
  minimum_billable_units: '',
  with_driver: false,
  fuel_lessee: true,
  fuel_rate_per_liter: '',
  start_date: aujourdHui(),
  end_date: '',
  deposit_amount: '',
  notes: '',
});

/**
 * Une case d'une carte de contrat: l'intitulé au-dessus, la valeur dessous.
 *
 * Elle rend des `<span>` et non un `<dt>/<dd>`: la carte entière est un
 * `<button>`, dont le contenu autorisé se limite au contenu de phrasé. Un
 * `<dl>` y serait du balisage invalide pour un gain nul — la relation
 * intitulé/valeur est déjà portée par la mise en page.
 */
const Champ = ({ label, children, className }) => (
  <span className="block min-w-0">
    <span className="block text-[0.68rem] font-bold uppercase tracking-[0.06em] text-muted-foreground">
      {label}
    </span>
    <span className={cn('mt-0.5 block truncate text-sm text-foreground', className)}>
      {children}
    </span>
  </span>
);

const RentalsPage = () => {
  const navigate = useNavigate();

  // Deux onglets pilotés par l'adresse (`?onglet=entrantes`): les camions que
  // je loue à des tiers (l'existant) et ceux que je prends en location chez un
  // transporteur (lot 018). Un lien partagé rouvre le bon onglet.
  const [searchParams, setSearchParams] = useSearchParams();
  const onglet = searchParams.get('onglet') === 'entrantes' ? 'entrantes' : 'sortantes';
  const [showCreateInbound, setShowCreateInbound] = useState(false);
  const choisirOnglet = (valeur) => {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (valeur === 'entrantes') next.set('onglet', 'entrantes');
        else {
          next.delete('onglet');
          next.delete('societe');
        }
        // Chaque onglet a sa liste et sa page: on repart de la première.
        ['page', 'taille', 'entrantes-page', 'entrantes-taille'].forEach((cle) => next.delete(cle));
        return next;
      },
      { replace: true },
    );
  };

  const [contracts, setContracts] = useState([]);
  const [total, setTotal] = useState(0);
  const [kpis, setKpis] = useState(null);
  const [trucks, setTrucks] = useState([]);

  const [isLoading, setIsLoading] = useState(true);
  // Le squelette ne remplace la page qu'au premier chargement: un
  // rafraîchissement démonterait le champ de recherche en cours de frappe.
  const [hasLoaded, setHasLoaded] = useState(false);

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search);
  const [statusFilter, setStatusFilter] = useState('all');
  const [modeFilter, setModeFilter] = useState('all');
  const [driverFilter, setDriverFilter] = useState('all');

  // Pagination commune: page et taille dans l'adresse, barre sous la liste.
  // Une recherche ou un filtre ramène à la première page.
  const pagination = usePaginationServeur(total, {
    reinitialiser: `${debouncedSearch}|${statusFilter}|${modeFilter}|${driverFilter}`,
  });
  const { page, limit: pageSize } = pagination;

  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(formulaireVide);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Le litre refacturé au retour est un prix de la société, pas une donnée du
  // contrat: les barèmes le portent déjà. Il est proposé, jamais imposé —
  // une location négociée l'emporte sur le tarif de référence.
  const [prixLitreTouche, setPrixLitreTouche] = useState(false);
  const { prix: prixLitrePropose, nomBareme } = usePrixPropose({
    operationType: 'RENTAL_FUEL',
    actif: showCreate,
  });

  useEffect(() => {
    if (!showCreate) return;
    setForm((prev) => ({
      ...prev,
      fuel_rate_per_liter: appliquerPrixPropose({
        prix: prixLitrePropose,
        valeurActuelle: prev.fuel_rate_per_liter,
        toucheParLHumain: prixLitreTouche,
      }),
    }));
  }, [prixLitrePropose, prixLitreTouche, showCreate]);

  const hasFilters =
    Boolean(debouncedSearch) ||
    statusFilter !== 'all' ||
    modeFilter !== 'all' ||
    driverFilter !== 'all';

  const nouvelleDemande = useDerniereDemande();
  const loadData = useCallback(async ({ silencieux = false } = {}) => {
    const perimee = nouvelleDemande();
    try {
      if (!silencieux) setIsLoading(true);
      // Les filtres partent au serveur: filtrer une page déjà découpée ferait
      // « perdre » des lignes dès la seconde page.
      const params = { page, page_size: pageSize };
      if (debouncedSearch) params.q = debouncedSearch;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (modeFilter !== 'all') params.pricing_mode = modeFilter;
      if (driverFilter !== 'all') params.with_driver = driverFilter === 'with';

      const [list, indicators] = await Promise.all([
        rentalsAPI.getContracts(params),
        rentalsAPI.getKpis(30),
      ]);
      if (perimee()) return;
      setContracts(list.items || []);
      setTotal(list.pagination?.total ?? (list.items || []).length);
      setKpis(indicators);
    } catch (error) {
      if (perimee()) return;
      console.error('Failed to load rental contracts:', error);
      toast.error(messageErreur(error, 'Impossible de charger les locations'));
    } finally {
      if (!perimee()) {
        setIsLoading(false);
        setHasLoaded(true);
      }
    }
  }, [page, pageSize, debouncedSearch, statusFilter, modeFilter, driverFilter, nouvelleDemande]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Une écriture faite ailleurs (autre écran, autre onglet, file hors ligne rejouée) recharge ces chiffres: jamais de compteur figé.
  useRafraichirSur(['locations', 'camions', 'factures', 'clients', 'paiements'], loadData);


  useEffect(() => {
    let cancelled = false;
    fetchAllPages((params) => trucksAPI.getAllPaged(params))
      .then((result) => {
        if (!cancelled) setTrucks(result.items || []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const truckLabel = useCallback(
    (truckId) => trucks.find((truck) => truck.id === truckId)?.matricule || 'Camion non précisé',
    [trucks],
  );
  const truckOptions = useMemo(() => trucks.map(truckOption), [trucks]);

  /* ------------------------------------------------------------------ */
  /* Les quatre compteurs                                                */
  /* ------------------------------------------------------------------ */

  const fleetSize = Number(kpis?.fleet_size) || 0;
  const rentedTrucks = Number(kpis?.rented_trucks) || 0;

  /**
   * Le taux d'occupation se déduit des deux compteurs affichés juste à côté,
   * et non d'un champ séparé: la barre de proportion et le pourcentage
   * viennent ainsi du même calcul et ne peuvent pas se contredire à l'écran.
   *
   * Sans parc connu il n'y a pas de dénominateur, donc pas de taux: on écrit
   * « — ». « 0 % » affirmerait qu'aucun camion n'est loué, ce qui est une
   * information — alors qu'on ne sait pas.
   */
  const occupancyRate = useMemo(
    () => (fleetSize > 0 ? Math.round((rentedTrucks / fleetSize) * 100) : null),
    [fleetSize, rentedTrucks],
  );

  const resetFilters = () => {
    setSearch('');
    setStatusFilter('all');
    setModeFilter('all');
    setDriverFilter('all');
  };

  const setField = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));

  const formErrors = useMemo(
    () => ({
      truck_id: !form.truck_id,
      lessee_label: !form.lessee_label.trim(),
      rate_amount: form.rate_amount === '' || Number(form.rate_amount) < 0,
      start_date: !form.start_date,
    }),
    [form],
  );

  /**
   * Le dépliant annonce ce qu'il contient, donc le compte suit ce qui y est
   * réellement rendu: les trois champs de dépassement n'existent pas à la
   * période, et le prix du litre n'existe pas si le loueur paie le carburant.
   * Annoncer « 8 champs » pour en montrer quatre est la façon la plus courte
   * de faire perdre confiance à un dépliant.
   */
  const nombreDetails =
    4 +
    (form.pricing_mode === 'period' ? 0 : 3) +
    (form.fuel_lessee ? 1 : 0);

  const handleCreate = async () => {
    setSubmitAttempted(true);
    if (Object.values(formErrors).some(Boolean)) {
      toast.error('Complétez les champs signalés');
      return;
    }

    setIsSubmitting(true);
    try {
      const toNumber = (value) => (value === '' ? null : Number(value));
      const created = await rentalsAPI.createContract({
        truck_id: form.truck_id,
        lessee_label: form.lessee_label.trim(),
        pricing_mode: form.pricing_mode,
        period_unit: form.pricing_mode === 'period' ? form.period_unit : null,
        rate_amount: Number(form.rate_amount),
        included_units: toNumber(form.included_units),
        overage_rate_amount: toNumber(form.overage_rate_amount),
        minimum_billable_units: toNumber(form.minimum_billable_units),
        with_driver: form.with_driver,
        fuel_responsibility: form.fuel_lessee ? 'lessee' : 'lessor',
        fuel_rate_per_liter: toNumber(form.fuel_rate_per_liter),
        start_date: form.start_date,
        end_date: form.end_date || null,
        deposit_amount: toNumber(form.deposit_amount),
        notes: form.notes || null,
      });
      toast.success(`Contrat ${created.contract_number} créé`);
      setShowCreate(false);
      setForm(formulaireVide());
      setSubmitAttempted(false);
      setPrixLitreTouche(false);
      navigate(`/rentals/${created.id}`);
    } catch (error) {
      console.error('Failed to create rental contract:', error);
      toast.error(messageErreur(error, 'Création impossible'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading && !hasLoaded) {
    return (
      <div className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8">
        <LoadingState shape="table" />
      </div>
    );
  }

  return (
    <div
      className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8"
      data-testid="rentals-page"
    >
      {/* La recherche transverse — celle qui croise client, camion et contrat —
          vit maintenant dans la barre supérieure de l'application, présente sur
          tous les écrans. La copie qui coiffait cette page faisait doublon: deux
          champs à l'écran, dont l'un ne faisait que rediriger vers `/search`.
          Le champ de la barre de filtres plus bas reste, lui: il cherche
          réellement dans les contrats. */}
      <PageHeader
        eyebrow="Parc"
        title="Locations de camions"
        description={
          onglet === 'entrantes'
            ? 'Camions que vous prenez en location chez un transporteur : contrat, relevé du mois, règlement du propriétaire.'
            : 'Camions citernes loués à des tiers : contrat, exécution, restitution.'
        }
        // Quatre chiffres, quatre états; les deux premiers portent un filtre
        // équivalent et l'appliquent au clic (lire « 4 camions hors flotte »
        // sans pouvoir voir lesquels serait une impasse).
        stats={
          onglet === 'entrantes'
            ? undefined
            : [
                {
                  label: 'Contrats en cours',
                  valeur: kpis?.active_contracts ?? 0,
                  aide: 'Signés et actifs',
                  onClick: () => setStatusFilter(statusFilter === 'active' ? 'all' : 'active'),
                },
                {
                  label: 'Camions loués',
                  valeur: rentedTrucks,
                  aide: fleetSize > 0 ? `Sur un parc de ${fleetSize} camions` : undefined,
                  ton: 'neutre',
                },
                {
                  label: 'Hors flotte',
                  valeur: kpis?.rented_out_trucks ?? 0,
                  aide: 'Loués sans chauffeur',
                  onClick: () => setDriverFilter(driverFilter === 'without' ? 'all' : 'without'),
                },
                {
                  label: 'Taux d’occupation',
                  valeur: occupancyRate === null ? '—' : `${occupancyRate} %`,
                  aide:
                    occupancyRate === null
                      ? 'Taille du parc inconnue'
                      : 'Part du parc en location',
                },
              ]
        }
        actions={
          onglet === 'entrantes' ? (
            <Button onClick={() => setShowCreateInbound(true)} data-testid="new-inbound-rental-btn">
              <Plus className="h-4 w-4" aria-hidden="true" />
              Nouveau contrat de location reçue
            </Button>
          ) : (
          <>
            {/* RENTED_OUT n'existe que grâce aux contrats de cet écran: le parc
                par état est la contrepartie imprimable des tuiles ci-dessous. */}
            <PrintMenu
              label="Imprimer"
              items={[
                {
                  label: 'Parc par état',
                  filename: 'parc-par-etat',
                  url: printingAPI.trucksByStatus,
                  formats: ['pdf', 'xlsx', 'csv'],
                  params: { include_rented: true },
                },
              ]}
            />
            <Button onClick={() => setShowCreate(true)} data-testid="new-rental-btn">
              <Plus className="h-4 w-4" aria-hidden="true" />
              Nouveau contrat
            </Button>
          </>
          )
        }
      />

      <Tabs value={onglet} onValueChange={choisirOnglet} className="space-y-5">
        <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1">
          <TabsTrigger value="sortantes" data-testid="rentals-tab-sortantes">
            Camions que je loue à des tiers
          </TabsTrigger>
          <TabsTrigger value="entrantes" data-testid="rentals-tab-entrantes">
            Camions que je prends en location
          </TabsTrigger>
        </TabsList>

        <TabsContent value="entrantes" className="mt-0">
          <InboundRentalsTab createOpen={showCreateInbound} onCreateOpenChange={setShowCreateInbound} />
        </TabsContent>

        <TabsContent value="sortantes" className="mt-0 space-y-5">

      <SectionCard
        icon={KeyRound}
        title="Contrats de location"
        // La barre du dessous cherche dans le numéro de contrat et le nom du
        // locataire, rien d'autre: le sous-titre dit ce qu'elle sait faire,
        // sinon on tape un matricule et on conclut qu'elle est cassée.
        subtitle="Recherchez un contrat par numéro ou par locataire."
        // Recherche et filtres dans l'en-tête de la carte, à droite du titre:
        // une rangée de moins avant le premier contrat.
        outils={
          <>
            <div className="relative w-full sm:w-64">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/70"
                aria-hidden="true"
              />
              <Input
                placeholder="Numéro de contrat ou locataire…"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className="input-base pl-10"
                aria-label="Rechercher un contrat"
                data-testid="rentals-search"
              />
            </div>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]" aria-label="Filtrer par statut">
                <SelectValue placeholder="Tous les statuts" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                {CONTRACT_STATUSES.map((item) => (
                  <SelectItem key={item.value} value={item.value}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={modeFilter} onValueChange={setModeFilter}>
              <SelectTrigger className="w-[170px]" aria-label="Filtrer par tarification">
                <SelectValue placeholder="Toutes tarifications" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes tarifications</SelectItem>
                {PRICING_MODES.map((item) => (
                  <SelectItem key={item.value} value={item.value}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={driverFilter} onValueChange={setDriverFilter}>
              <SelectTrigger className="w-[190px]" aria-label="Filtrer par chauffeur">
                <SelectValue placeholder="Avec ou sans chauffeur" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Avec ou sans chauffeur</SelectItem>
                <SelectItem value="with">Avec chauffeur</SelectItem>
                <SelectItem value="without">Sans chauffeur</SelectItem>
              </SelectContent>
            </Select>

            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={resetFilters}>
                <X className="h-4 w-4" aria-hidden="true" />
                Effacer
              </Button>
            )}

            {/* Le décompte porte sur le total serveur, pas sur la page affichée:
                « 3 contrats » alors que la base en compte 78 serait un mensonge. */}
            <span className="text-xs text-muted-foreground">
              {total} contrat{total > 1 ? 's' : ''}
            </span>
          </>
        }
        contentClassName="pt-3"
      >

        {contracts.length === 0 ? (
          hasFilters ? (
            // Filtré à vide: l'écran est déjà occupé par la barre de filtres,
            // l'icône suffit — et la sortie est d'effacer, pas de créer.
            <EmptyState
              variant="filtered"
              icon={KeyRound}
              title="Aucun contrat ne correspond"
              description="Vos contrats ne sont pas perdus : ils sont simplement masqués par les filtres actifs. Effacez-les pour tout revoir."
              actionLabel="Effacer les filtres"
              onAction={resetFilters}
            />
          ) : (
            <EmptyState
              illustration="donnee"
              title="Aucune location enregistrée"
              description="Une location met un de vos camions à disposition d’un tiers pour une durée et un loyer convenus. Le contrat suit les échéances de paiement, la restitution et l’état du véhicule."
              // Une location porte sur un camion précis: sans parc, le
              // sélecteur du formulaire est vide et la création ne peut pas
              // aboutir. On ne loue pas un camion qu'on n'a pas — le premier
              // pas est donc le parc, pas le contrat.
              actionLabel={
                trucks.length === 0 ? 'Ajouter un camion d’abord' : '+ Créer un contrat'
              }
              onAction={
                trucks.length === 0
                  ? () => navigate('/trucks')
                  : () => setShowCreate(true)
              }
            />
          )
        ) : (
          <div className="space-y-2.5">
            {contracts.map((contract) => (
              <button
                key={contract.id}
                type="button"
                onClick={() => navigate(`/rentals/${contract.id}`)}
                data-testid={`rental-row-${contract.id}`}
                className="block w-full rounded-xl border border-border/70 bg-card p-4 text-left transition-colors hover:border-border hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                {/* L'identité du contrat en tête, le statut à l'opposé: c'est
                    la paire qu'on balaie en diagonale pour trier une liste. */}
                <span className="mb-3 flex flex-wrap items-start justify-between gap-3">
                  <span className="min-w-0">
                    <span className="block font-mono text-xs font-semibold text-muted-foreground">
                      {contract.contract_number}
                    </span>
                    <span className="mt-0.5 block truncate text-sm font-bold text-foreground">
                      {contract.lessee_label || 'Locataire non précisé'}
                    </span>
                  </span>
                  {/* `status-badge` plutôt que le composant `Badge`: celui-ci
                      rend un `<div>`, illégal dans un `<button>`. La classe
                      donne exactement la même pastille. */}
                  <span
                    className={cn(
                      'status-badge shrink-0',
                      STATUS_STYLES[contract.status] || STATUS_STYLES.draft,
                    )}
                  >
                    {statusLabel(contract.status)}
                  </span>
                </span>

                <span className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3 xl:grid-cols-6">
                  <Champ label="Camion" className="font-mono text-xs">
                    {truckLabel(contract.truck_id)}
                  </Champ>
                  <Champ label="Tarification">{pricingLabel(contract)}</Champ>
                  <Champ label="Tarif" className="tabular-nums">
                    {formatMoney(contract.rate_amount, contract.currency)}
                  </Champ>
                  <Champ label="Chauffeur">
                    {contract.with_driver ? 'Avec' : 'Sans'}
                  </Champ>
                  <Champ label="Carburant">
                    {contract.fuel_responsibility === 'lessee' ? 'Locataire' : 'Loueur'}
                  </Champ>
                  {/* Une fin absente n'est pas une donnée manquante: elle veut
                      dire « en cours, sans terme convenu ». D'où « sans date de
                      fin » et non le « — » des valeurs qu'on ignore. */}
                  <Champ label="Période" className="whitespace-nowrap text-xs">
                    {formatDateOuTiret(contract.start_date)} → {contract.end_date ? formatDateOuTiret(contract.end_date) : 'sans date de fin'}
                  </Champ>
                </span>
              </button>
            ))}
          </div>
        )}

        {contracts.length > 0 ? <BarrePagination
            pagination={pagination}
            nom="contrats"
            className="mt-4 rounded-none border-0 border-t border-border/70 bg-transparent px-0 pb-0"
          /> : null}
      </SectionCard>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Nouveau contrat de location</DialogTitle>
            <DialogDescription>
              Le mode de tarification décide des champs suivants. Le contrat démarre en
              brouillon : vous l'activerez après l'état des lieux de départ.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Le mode de tarification vient en premier parce qu'il conditionne
                tout le reste du formulaire. */}
            <div className="space-y-2">
              <Label>Mode de tarification</Label>
              <div className="grid gap-2 sm:grid-cols-3">
                {PRICING_MODES.map((mode) => (
                  <button
                    key={mode.value}
                    type="button"
                    onClick={() => setField('pricing_mode', mode.value)}
                    aria-pressed={form.pricing_mode === mode.value}
                    className={cn(
                      'rounded-lg border p-3 text-left transition-colors',
                      form.pricing_mode === mode.value
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:bg-muted/50',
                    )}
                  >
                    <span className="block text-sm font-medium">{mode.label}</span>
                    <span className="block text-xs text-muted-foreground">{mode.hint}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="rental-truck">Camion</Label>
                <SearchableSelect
                  id="rental-truck"
                  placeholder="Choisir un camion"
                  searchPlaceholder="Rechercher une immatriculation…"
                  emptyMessage="Aucun camion trouvé"
                  value={form.truck_id || ''}
                  onValueChange={(value) => setField('truck_id', value)}
                  options={truckOptions}
                />
                <FieldError show={submitAttempted && formErrors.truck_id}>
                  Choisissez le camion à louer.
                </FieldError>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="rental-lessee">Locataire</Label>
                <Input
                  id="rental-lessee"
                  value={form.lessee_label}
                  onChange={(event) => setField('lessee_label', event.target.value)}
                  placeholder="Nom de la société locataire"
                />
                <FieldError show={submitAttempted && formErrors.lessee_label}>
                  Indiquez à qui le camion est loué.
                </FieldError>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              {form.pricing_mode === 'period' && (
                <div className="space-y-1.5">
                  <Label htmlFor="rental-unit">Unité de période</Label>
                  <Select
                    value={form.period_unit}
                    onValueChange={(value) => setField('period_unit', value)}
                  >
                    <SelectTrigger id="rental-unit">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PERIOD_UNITS.map((unit) => (
                        <SelectItem key={unit.value} value={unit.value}>
                          {unit.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="rental-rate">
                  {form.pricing_mode === 'liter'
                    ? 'Prix du litre'
                    : form.pricing_mode === 'rotation'
                      ? 'Forfait par rotation'
                      : 'Loyer par unité'}
                </Label>
                <Input
                  id="rental-rate"
                  type="number"
                  min="0"
                  value={form.rate_amount}
                  onChange={(event) => setField('rate_amount', event.target.value)}
                />
                <FieldError show={submitAttempted && formErrors.rate_amount}>
                  Saisissez un montant positif.
                </FieldError>
              </div>
            </div>

            <div className="space-y-3 rounded-lg border border-border p-3">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-1">
                    <Label htmlFor="rental-driver">Location avec chauffeur</Label>
                    <Aide titre="Location avec chauffeur">
                      Sans chauffeur, le camion sort de la flotte et ne peut plus partir en
                      trajet.
                    </Aide>
                  </div>
                </div>
                <Switch
                  id="rental-driver"
                  checked={form.with_driver}
                  onCheckedChange={(checked) => setField('with_driver', checked)}
                />
              </div>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <Label htmlFor="rental-fuel">Carburant à la charge du locataire</Label>
                  <p className="text-xs text-muted-foreground">
                    Le carburant manquant au retour sera refacturé.
                  </p>
                </div>
                <Switch
                  id="rental-fuel"
                  checked={form.fuel_lessee}
                  onCheckedChange={(checked) => setField('fuel_lessee', checked)}
                />
              </div>
            </div>

            {/* Neuf champs sur quinze ne se remplissaient pas: trois portaient
                déjà « Facultatif », le prix du litre sort des barèmes et la
                date de début est celle du jour. Ils restent tous accessibles —
                un dépassement à refacturer ou une caution se saisissent ici. */}
            <ChampsAvances
              titre="Plus de détails"
              consequence="Dépassement, caution, dates de fin et notes du contrat."
              nombre={nombreDetails}
              valeurs={[
                // La date de début et le prix du litre sont posés par le
                // formulaire, pas saisis: les compter ouvrirait le repli à
                // chaque nouveau contrat.
                form.included_units,
                form.overage_rate_amount,
                form.minimum_billable_units,
                form.end_date,
                form.deposit_amount,
                form.notes,
              ]}
            >
              {form.pricing_mode !== 'period' && (
                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="rental-included">Unités comprises</Label>
                    <Input
                      id="rental-included"
                      type="number"
                      min="0"
                      value={form.included_units}
                      onChange={(event) => setField('included_units', event.target.value)}
                      placeholder="Facultatif"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="rental-overage">Tarif de dépassement</Label>
                    <Input
                      id="rental-overage"
                      type="number"
                      min="0"
                      value={form.overage_rate_amount}
                      onChange={(event) => setField('overage_rate_amount', event.target.value)}
                      placeholder="Facultatif"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="rental-minimum">Plancher facturable</Label>
                    <Input
                      id="rental-minimum"
                      type="number"
                      min="0"
                      value={form.minimum_billable_units}
                      onChange={(event) =>
                        setField('minimum_billable_units', event.target.value)
                      }
                      placeholder="Facultatif"
                    />
                  </div>
                </div>
              )}

              {form.fuel_lessee && (
                <div className="space-y-1.5">
                  <Label htmlFor="rental-fuel-rate">Prix du litre refacturé</Label>
                  <Input
                    id="rental-fuel-rate"
                    type="number"
                    min="0"
                    value={form.fuel_rate_per_liter}
                    onChange={(event) => {
                      setPrixLitreTouche(true);
                      setField('fuel_rate_per_liter', event.target.value);
                    }}
                  />
                  {nomBareme && !prixLitreTouche ? (
                    <p className="text-xs text-muted-foreground">
                      Barème «&nbsp;{nomBareme}&nbsp;» — corrigez-le si le contrat prévoit un autre prix.
                    </p>
                  ) : null}
                </div>
              )}

              <div className="grid gap-3 sm:grid-cols-3">
                <div className="space-y-1.5">
                  <Label htmlFor="rental-start">Début</Label>
                  <Input
                    id="rental-start"
                    type="date"
                    value={form.start_date}
                    onChange={(event) => setField('start_date', event.target.value)}
                  />
                  <FieldError show={submitAttempted && formErrors.start_date}>
                    La date de début est obligatoire.
                  </FieldError>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="rental-end">Fin prévue</Label>
                  <Input
                    id="rental-end"
                    type="date"
                    value={form.end_date}
                    onChange={(event) => setField('end_date', event.target.value)}
                    placeholder="Facultatif"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="rental-deposit">Caution</Label>
                  <Input
                    id="rental-deposit"
                    type="number"
                    min="0"
                    value={form.deposit_amount}
                    onChange={(event) => setField('deposit_amount', event.target.value)}
                    placeholder="Facultatif"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="rental-notes">Notes</Label>
                <Textarea
                  id="rental-notes"
                  rows={2}
                  value={form.notes}
                  onChange={(event) => setField('notes', event.target.value)}
                  placeholder="Facultatif"
                />
              </div>
            </ChampsAvances>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>
              Annuler
            </Button>
            <Button onClick={handleCreate} disabled={isSubmitting}>
              {isSubmitting ? 'Création…' : 'Créer le contrat'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default RentalsPage;
